/**
 * ============================================================================
 * CheckoutCancelPage.js - Stripe Checkout Cancel Handler
 * ============================================================================
 * Displayed when user cancels or exits Stripe checkout
 * Created: January 27, 2026 - Phase 3D Subscription Billing
 * 
 * FEATURES:
 * - Friendly message that payment was not completed
 * - Options to try again or start with trial
 * - Links to contact support
 * - Professional design matching success page
 * ============================================================================
 */

import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

function CheckoutCancelPage() {
  const navigate = useNavigate();
  
  // Log checkout abandonment on mount
  useEffect(() => {
    const orgId = sessionStorage.getItem('checkoutOrgId') || sessionStorage.getItem('pendingOrgId');
    if (orgId) {
      fetch(`${API_URL}/stripe/log-abandonment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: orgId, reason: 'stripe_checkout_cancelled' })
      }).catch(() => {}); // Silent - tracking shouldn't break flow
      
      // Clean up session storage
      sessionStorage.removeItem('checkoutOrgId');
      sessionStorage.removeItem('pendingOrgId');
      sessionStorage.removeItem('pendingAccessToken');
      sessionStorage.removeItem('pendingRefreshToken');
    }
  }, []);
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 24,
        padding: 48,
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 25px 80px rgba(0,0,0,0.12), 0 10px 30px rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Top accent bar - amber/yellow for cancel */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 6,
          background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)'
        }} />
        
        {/* Icon */}
        <div style={{
          width: 100,
          height: 100,
          margin: '0 auto 28px',
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 40px rgba(245, 158, 11, 0.25)'
        }}>
          <span style={{ fontSize: 50 }}>🔙</span>
        </div>
        
        {/* Title */}
        <h1 style={{ 
          fontSize: 28, 
          fontWeight: 800, 
          color: '#1f2937', 
          marginBottom: 12
        }}>
          Registration Cancelled
        </h1>
        
        <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 28, lineHeight: 1.6 }}>
          No worries! Your card was not charged.
          <br />
          Your account has been created — you can complete setup when you're ready.
        </p>
        
        {/* Options Card */}
        <div style={{
          background: '#f9fafb',
          borderRadius: 16,
          padding: 24,
          marginBottom: 28
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 16 }}>
            What would you like to do?
          </div>
          
          {/* Option 1: Try Again */}
          <button
            onClick={() => navigate('/register')}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: 12,
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(45, 106, 79, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(45, 106, 79, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(45, 106, 79, 0.3)';
            }}
          >
            🔄 Try Again
          </button>
          
          {/* Option 2: Go to Landing Page */}
          <button
            onClick={() => navigate('/')}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: '#fff',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f9fafb';
              e.target.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#fff';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            ← Back to Home
          </button>
        </div>
        
        {/* Already have account */}
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24
        }}>
          <p style={{ margin: 0, fontSize: 14, color: '#166534' }}>
            Already have an account?{' '}
            <Link 
              to="/login" 
              style={{ 
                color: '#15803d', 
                fontWeight: 600, 
                textDecoration: 'none' 
              }}
            >
              Sign in here →
            </Link>
          </p>
        </div>
        
        {/* Help Section */}
        <div style={{
          borderTop: '1px solid #e5e7eb',
          paddingTop: 20
        }}>
          <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 8 }}>
            Having trouble with payment?
          </p>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
            Contact us at{' '}
            <a 
              href="mailto:support@teetimeorganizer.net" 
              style={{ color: '#2d6a4f', textDecoration: 'none' }}
            >
              support@teetimeorganizer.net
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CheckoutCancelPage;
